export interface QueryMetadata {
  raw_sql: string
  execution_time_ms: number | null
  rows_scanned: number
  cached: boolean
}

export const BILBRANSJE_CLASSES = [
  'BILBRANSJE_TO_PERSON',
  'BILBRANSJE_TO_FIRMA',
  'BILBRANSJE_TO_BILUTLEIE',
] as const

export type BilbransjeClass = typeof BILBRANSJE_CLASSES[number]

export interface FiltersApplied {
  start_date: string
  end_date: string
  dealer_postnummer: string | null
  buyer_postnummer: string | null
  varebil: boolean | null
  classes: string[]
}

export interface BankRef {
  org_nr: string
  org_name: string
}

export interface OverviewKpis {
  agent_count: number
  total_agent_transfers: number
  in_scope_sales: number
  financed_sales: number
  bank_financed_sales: number
  bank_market_share: number
  bank_total_nok: number
}

export interface BankShareSlice {
  bank_name: string
  bank_org_nr: string | null
  count: number
  share: number
  total_nok: number
}

export interface WeeklyTrendPoint {
  week_start: string
  week_number: number
  bank_count: number
  total_count: number
  bank_share: number        // 0..1
  financed_count: number    // transfers with any active loan this week
  financed_share: number    // financed_count / total_count (0..1)
  bank_total_nok: number    // sum of this bank's loan amounts this week (NOK)
}

export interface ConfidenceBreakdownSlice {
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN'
  count: number
  total_nok: number
  share_of_financed: number
}

export interface BruktbilfinansieringOverviewResponse {
  bank: BankRef
  kpis: OverviewKpis
  bank_share_pie: BankShareSlice[]
  weekly_trend: WeeklyTrendPoint[]
  confidence_breakdown: ConfidenceBreakdownSlice[]
  loan_data_complete_through: string  // ISO date; weeks after this may have incomplete loan data
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

export interface TopCompetingBank {
  org_nr: string | null
  name: string
  count: number
  share: number
}

export interface DealerRow {
  dealer_org_nr: string
  dealer_name: string
  dealer_home_postnummer: string | null
  dealer_home_kommune: string | null
  dealer_home_fylke: string | null
  total_in_scope_sales: number
  total_all_sales: number
  financed_sales: number
  bank_financed_count: number
  bank_market_share: number
  bank_total_nok: number
  total_loans_all_banks: number
  total_volume_all_banks: number
  top_competing_bank: TopCompetingBank | null
}

export interface DealersResponse {
  data: DealerRow[]
  total_records: number
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// Datakvalitet types

export interface FreshnessCard {
  latest_transfer_event_at: string | null
  latest_brreg_check_at: string | null
  latest_classifier_run_at: string | null
  classifier_version: string | null
  latest_brreg_loan_run_at: string | null
  latest_financed_transfer_event_at: string | null
}

export interface ScopeClassRow {
  class_label: string
  transfer_count: number
  in_scope: boolean
}

export type CoverageFunnelStepName =
  | 'in_scope_sales' | 'with_brreg_row' | 'loan_active' | 'bank_financed'

export interface CoverageFunnelStep {
  step: CoverageFunnelStepName
  count: number
  pct_of_previous: number | null
  pct_of_in_scope: number
}

export interface BrregPendingBlock {
  pending_count: number
  oldest_pending_event_at: string | null
}

export interface LoanPairingBlock {
  paired_in_scope_loan_count: number
  paired_out_of_scope_loan_count: number
  unpaired_loan_count: number
  unpaired_in_scope_cars: number
}

export interface MatchBreakdownRow {
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  reason: string
  count: number
  total_nok: number
}

export interface RejectBreakdownRow {
  reject_reason: string
  count: number
  best_score_avg: number | null
}

export interface BankMarketRow {
  bank_org_nr: string
  bank_name: string
  financed_count: number
  total_nok: number
  market_share_pct: number
}

export interface BankMarketResponse {
  rows: BankMarketRow[]
  total_financed: number
  total_nok: number
}

/** GET /{bank}/freshness — the freshness block on its own (bilpriserproff#281). */
export interface FreshnessResponse {
  bank: BankRef
  freshness: FreshnessCard
  query_metadata: QueryMetadata
}

export interface DatakvalitetResponse {
  bank: BankRef
  freshness: FreshnessCard
  scope_breakdown: ScopeClassRow[]
  coverage_funnel: CoverageFunnelStep[]
  brreg_pending: BrregPendingBlock
  loan_pairing: LoanPairingBlock
  match_breakdown: MatchBreakdownRow[]
  reject_breakdown: RejectBreakdownRow[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

export interface CrossBankDealerRow {
  forhandler_org_nr: string
  forhandler_navn: string
  bruktbiler: number
  oppkjoperbiler: number
  andel_oppkjop: number
  bruktbiler_netto: number
  laan: number
  andel_laan: number
  DNB: number
  SAN: number
  NOR: number
  VWF: number
  TFS: number
  BMW: number
  BRA: number
  FIN: number
  SPN: number
  SPO: number
  SPM: number
  AND: number
}

export interface CrossBankDealersResponse {
  data: CrossBankDealerRow[]
  total_records: number
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

export interface DealerBankSlice {
  bank_org_nr: string
  bank_name: string
  loan_count: number
  total_belop: number
  share: number
}

export interface BrandTopBank {
  bank_name: string
  bank_short_code: string
  loan_count: number
  share: number
}

export interface DealerBrandRow {
  merke: string
  total_sales: number
  financed_count: number
  finance_rate: number
  avg_car_age: number | null
  median_car_age: number | null
  top_banks: BrandTopBank[]
}

export type BuyerKind = 'person' | 'org' | 'unknown'

export interface DealerBuyerRow {
  omregistreringsdato: string
  merke: string | null
  modell: string | null
  financed: boolean
  bank_name: string | null
  bank_belop: number | null
  buyer_kind: BuyerKind
  buyer_org_navn: string | null
  buyer_kommune: string | null   // no buyer age/postnummer per row (#912)
  registrert_forste_gang: string | null
  tinglysingsdato: string | null
  loan_confidence: 'HIGH' | 'MEDIUM' | 'LOW' | null
}

export interface DealerKpis {
  dealer_org_nr: string
  dealer_name: string
  total_sales: number
  financed_sales: number
  finance_rate: number
  total_loan_nok: number
  avg_loan_nok: number
  oppkjoperbiler: number
  bruktbiler_netto: number
  borrower_avg_age: number | null
  buyer_avg_age: number | null
  total_all_sales: number
  total_loans_all_banks: number
  total_volume_all_banks: number
  bank_financed_count: number
  bank_market_share: number
  bank_total_nok: number
}

export interface DealerDetailsResponse {
  kpis: DealerKpis
  bank_breakdown: DealerBankSlice[]
  brand_mix: DealerBrandRow[]
  buyer_rows: DealerBuyerRow[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// ---------------------------------------------------------------------------
// Solgte biler — "Uten finansieringsdata" tab (#407)
// ---------------------------------------------------------------------------

/** One row in the loan-free Solgte biler grid.
 *
 *  Not an extension of DealerBuyerRow: this row carries no loan data at all,
 *  which is what lets its window run to today instead of stopping at the
 *  15-day Brreg lag.
 */
export interface DealerSoldRow {
  hendelse_tidspunkt: string      // ISO datetime — has time-of-day, unlike DealerBuyerRow
  kjennemerke: string | null
  merke: string | null
  modell: string | null
  klasse: string | null
  forstegang_reg: string | null
  registrert_pa_eier: string | null   // null when the car was never registered on the buyer
  buyer_kind: BuyerKind
  buyer_org_navn: string | null
  buyer_kommune: string | null   // no buyer age/postnummer per row (#912)
}

export interface DealerSoldRowsResponse {
  bank: BankRef
  dealer_org_nr: string
  dealer_name: string
  data_through: string            // MAX(hendelse_tidspunkt) — the horizon to show the user
  rows: DealerSoldRow[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// ---------------------------------------------------------------------------
// Klasser tab — all scope=1 transfer classes
// ---------------------------------------------------------------------------

export const ALL_SCOPE1_CLASSES = [
  'BILBRANSJE_TO_PERSON', 'BILBRANSJE_TO_FIRMA', 'BILBRANSJE_TO_BILUTLEIE',
  'PERSON_TO_PERSON',     'PERSON_TO_FIRMA',     'PERSON_TO_BILUTLEIE',
  'FIRMA_TO_PERSON',      'FIRMA_TO_FIRMA',      'FIRMA_TO_BILUTLEIE',
  'BILUTLEIE_TO_PERSON',  'BILUTLEIE_TO_FIRMA',  'BILUTLEIE_TO_BILUTLEIE',
  'LEASING_TO_PERSON',    'LEASING_TO_FIRMA',    'LEASING_TO_BILUTLEIE',
  'FORSIKRING_TO_PERSON', 'FORSIKRING_TO_FIRMA', 'FORSIKRING_TO_BILUTLEIE',
] as const

export type Scope1Class = typeof ALL_SCOPE1_CLASSES[number]

// Grouped for the multiselect UI
export const SCOPE1_CLASS_GROUPS: Record<string, Scope1Class[]> = {
  BILBRANSJE: ['BILBRANSJE_TO_PERSON', 'BILBRANSJE_TO_FIRMA', 'BILBRANSJE_TO_BILUTLEIE'],
  PERSON:     ['PERSON_TO_PERSON',     'PERSON_TO_FIRMA',     'PERSON_TO_BILUTLEIE'],
  FIRMA:      ['FIRMA_TO_PERSON',      'FIRMA_TO_FIRMA',      'FIRMA_TO_BILUTLEIE'],
  BILUTLEIE:  ['BILUTLEIE_TO_PERSON',  'BILUTLEIE_TO_FIRMA',  'BILUTLEIE_TO_BILUTLEIE'],
  LEASING:    ['LEASING_TO_PERSON',    'LEASING_TO_FIRMA',    'LEASING_TO_BILUTLEIE'],
  FORSIKRING: ['FORSIKRING_TO_PERSON', 'FORSIKRING_TO_FIRMA', 'FORSIKRING_TO_BILUTLEIE'],
}

// Scope=0 classes: seller → BILBRANSJE / FORSIKRING / LEASING (self-loops and cross-loops)
// Discovered via: SELECT DISTINCT class FROM SVVTransfers ORDER BY class
export const ALL_SCOPE0_CLASSES = [
  'BILBRANSJE_TO_BILBRANSJE', 'BILBRANSJE_TO_FORSIKRING', 'BILBRANSJE_TO_LEASING',
  'BILUTLEIE_TO_BILBRANSJE',  'BILUTLEIE_TO_FORSIKRING',  'BILUTLEIE_TO_LEASING',
  'FIRMA_TO_BILBRANSJE',      'FIRMA_TO_FORSIKRING',       'FIRMA_TO_LEASING',
  'FORSIKRING_TO_BILBRANSJE', 'FORSIKRING_TO_FORSIKRING',
  'LEASING_TO_BILBRANSJE',    'LEASING_TO_FORSIKRING',    'LEASING_TO_LEASING',
  'PERSON_TO_BILBRANSJE',     'PERSON_TO_FORSIKRING',     'PERSON_TO_LEASING',
] as const

export type Scope0Class = typeof ALL_SCOPE0_CLASSES[number]

export const SCOPE0_CLASS_GROUPS: Record<string, Scope0Class[]> = {
  BILBRANSJE: ['BILBRANSJE_TO_BILBRANSJE', 'BILBRANSJE_TO_FORSIKRING', 'BILBRANSJE_TO_LEASING'],
  BILUTLEIE:  ['BILUTLEIE_TO_BILBRANSJE',  'BILUTLEIE_TO_FORSIKRING',  'BILUTLEIE_TO_LEASING'],
  FIRMA:      ['FIRMA_TO_BILBRANSJE',       'FIRMA_TO_FORSIKRING',      'FIRMA_TO_LEASING'],
  FORSIKRING: ['FORSIKRING_TO_BILBRANSJE',  'FORSIKRING_TO_FORSIKRING'],
  LEASING:    ['LEASING_TO_BILBRANSJE',     'LEASING_TO_FORSIKRING',    'LEASING_TO_LEASING'],
  PERSON:     ['PERSON_TO_BILBRANSJE',      'PERSON_TO_FORSIKRING',     'PERSON_TO_LEASING'],
}

// All 35 transfer classes (scope=1 + scope=0)
export const ALL_36_CLASSES = [...ALL_SCOPE1_CLASSES, ...ALL_SCOPE0_CLASSES] as const
export type AnyClass = typeof ALL_36_CLASSES[number]

// Merged groups per seller type (scope=1 + scope=0 combined under the same key)
export const ALL_CLASS_GROUPS: Record<string, string[]> = {
  BILBRANSJE: [...SCOPE1_CLASS_GROUPS.BILBRANSJE, ...SCOPE0_CLASS_GROUPS.BILBRANSJE],
  PERSON:     [...SCOPE1_CLASS_GROUPS.PERSON,     ...SCOPE0_CLASS_GROUPS.PERSON],
  FIRMA:      [...SCOPE1_CLASS_GROUPS.FIRMA,      ...SCOPE0_CLASS_GROUPS.FIRMA],
  BILUTLEIE:  [...SCOPE1_CLASS_GROUPS.BILUTLEIE,  ...SCOPE0_CLASS_GROUPS.BILUTLEIE],
  LEASING:    [...SCOPE1_CLASS_GROUPS.LEASING,    ...SCOPE0_CLASS_GROUPS.LEASING],
  FORSIKRING: [...SCOPE1_CLASS_GROUPS.FORSIKRING, ...SCOPE0_CLASS_GROUPS.FORSIKRING],
}

export interface TransferClassRow {
  class_label: string
  total_transfers: number
  transfers_with_loan: number
  loan_ratio_pct: number
  bank_loan_count: number
  bank_financing_share_pct: number
  bank_total_belop: number
  bank_avg_belop: number | null
  other_bank_loan_count: number
  other_bank_total_belop: number
  bank_market_share_count_pct: number
  bank_market_share_volume_pct: number
}

export interface TransferClassKpis {
  total_transfers: number
  total_transfers_with_loan: number
  overall_loan_ratio_pct: number
  total_bank_loan_count: number
  overall_bank_financing_share_pct: number
  total_bank_belop: number
  total_other_bank_loan_count: number
  total_other_bank_belop: number
  overall_bank_market_share_count_pct: number
  overall_bank_market_share_volume_pct: number
}

export interface TransferClassesResponse {
  bank: BankRef
  kpis: TransferClassKpis
  rows: TransferClassRow[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// ---------------------------------------------------------------------------
// Klasser details (drill-down) — one class, all banks
// ---------------------------------------------------------------------------

export interface ClassBankRow {
  rank: number
  bank_org_nr: string
  bank_name: string
  is_self: boolean
  loan_count: number
  share_count_pct: number
  total_belop: number
  avg_belop: number | null
  share_volume_pct: number
}

export interface ClassDetailsKpis {
  total_transfers: number
  total_named_loans: number
  bank_loan_count: number
  bank_rank_by_count: number | null
  bank_share_count_pct: number
  bank_share_volume_pct: number
  distinct_named_banks: number
}

export interface KlasserClassDetailsResponse {
  bank: BankRef
  class_label: string
  kpis: ClassDetailsKpis
  rows: ClassBankRow[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// ---------------------------------------------------------------------------
// Loan-amount profile — multi-bank, multi-class line chart
// ---------------------------------------------------------------------------

export interface LoanAmountPoint {
  amount_nok: number
  count: number
  percentage: number
}

export interface BankProfile {
  bank_org_nr: string
  bank_name: string
  is_self: boolean
  total_loans: number
  over_max_count: number
  points: LoanAmountPoint[]
}

export interface LoanAmountProfileResponse {
  bank: BankRef
  profiles: BankProfile[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// ---------------------------------------------------------------------------
// Borrower-age profile — multi-bank, multi-class line chart
// ---------------------------------------------------------------------------

export interface AgePoint {
  age: number
  count: number
  percentage: number
}

export interface BorrowerAgeProfile {
  bank_org_nr: string
  bank_name: string
  is_self: boolean
  total_borrowers: number
  over_max_count: number
  points: AgePoint[]
}

export interface BorrowerAgeProfileResponse {
  bank: BankRef
  profiles: BorrowerAgeProfile[]
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// Bilalder tab — financing share bucketed by car age at transfer time
export interface CarAgeBucketRow {
  bucket_label: string
  bucket_order: number
  antall_biler: number
  antall_lan: number
  laneandel: number          // 0..1
  volum: number
  bankens_lan: number
  bankens_volum: number
  bankens_markedsandel: number    // 0..1
  bankens_lan_share: number       // 0..1 — bucket's share of the user-bank's total loan count
  bankens_volum_share: number     // 0..1 — bucket's share of the user-bank's total volume
}

export interface CarAgePoint {
  bucket_label: string
  bucket_order: number
  loan_count: number
  loan_volume: number
  market_share: number       // 0..1
  own_count_share: number    // 0..1 — bucket's share of THIS bank's total count
  own_volume_share: number   // 0..1 — bucket's share of THIS bank's total volume
}

export interface CarAgeProfile {
  bank_org_nr: string
  bank_name: string
  is_self: boolean
  total_loans: number
  total_volume: number
  points: CarAgePoint[]
}

export interface CarAgeBucketResponse {
  bank: BankRef
  rows: CarAgeBucketRow[]
  profiles: CarAgeProfile[]   // empty unless compare_bank_org_nrs provided
  query_metadata: QueryMetadata
  filters_applied: FiltersApplied
}

// ---------------------------------------------------------------------------
// Hendelser tab — dealer-scoped vehicle events (#408)
// ---------------------------------------------------------------------------

export type HendelseType = 'eie' | 'kjopt' | 'solgt'
export type HendelseSource = 'nybil' | 'bruktbil'

export interface HendelseRow {
  hendelse_dato: string                  // ISO date
  hendelse_tidspunkt: string | null      // null for nyreg — SVV gives no time of day
  type: HendelseType
  kjennemerke: string | null
  merke: string | null
  modell: string | null
  forstegang_reg: string | null
  org_nr: string | null                  // the dealer, not the counterparty
  org_navn: string | null
  postnummer: string | null
  source: HendelseSource
}

export interface HendelseSummary {
  total: number
  eie_count: number
  kjopt_count: number
  solgt_count: number
}

export interface HendelsePagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface HendelseDealerRef {
  org_nr: string
  org_navn: string
}

export interface HendelseDealersResponse {
  bank: BankRef
  dealers: HendelseDealerRef[]
}

export interface HendelserResponse {
  bank: BankRef
  summary: HendelseSummary
  rows: HendelseRow[]
  pagination: HendelsePagination
  query_metadata: QueryMetadata
  filters_applied: {
    start_date: string
    end_date: string
    type: HendelseType | null
    source: HendelseSource | null
    dealer_org_nr: string | null
  }
}
